import { useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import ComicCard from "@/components/base/ComicCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { mangaCategories, getCategoryBySlug } from "@/mocks/categories";
import { mangaList } from "@/mocks/comics";
import { parseViewsString } from "@/utils/format";

const SITE_BASE = "https://kisscorner.github.io";

const SORT_OPTIONS = [
  { key: "hot", label: "人气最高" },
  { key: "new", label: "最新更新" },
  { key: "rating", label: "高分好评" },
] as const;

type SortKey = (typeof SORT_OPTIONS)[number]["key"];

export default function CategoryPage() {
  const { slug = "all" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q")?.trim() || "";
  const currentCategory = getCategoryBySlug(slug);
  const isAll = slug === "all";

  const filtered = useMemo(() => {
    let list = isAll ? [...mangaList] : mangaList.filter((m) => m.categorySlugs.includes(slug));
    if (query) {
      const q = query.toLowerCase();
      list = list.filter(
        (m) => m.title.toLowerCase().includes(q) || m.tags.some((t) => t.toLowerCase().includes(q))
      );
    }
    return list;
  }, [slug, isAll, query]);

  const [sort, setSort] = useState<SortKey>("hot");

  const sorted = useMemo(() => {
    const list = [...filtered];
    if (sort === "new") {
      return list.sort((a, b) => (a.updateDate < b.updateDate ? 1 : -1));
    }
    if (sort === "rating") {
      return list.sort((a, b) => b.rating - a.rating);
    }
    return list.sort((a, b) => parseViewsString(b.views) - parseViewsString(a.views));
  }, [filtered, sort]);

  const pageTitle = isAll
    ? query
      ? `搜索：${query} - 89890漫画网`
      : "全部漫画 - 免费在线阅读 - 89890漫画网"
    : `${currentCategory?.name || ""}漫画在线免费阅读 - 89890漫画网`;

  const pageDesc = isAll
    ? "89890漫画网全部漫画免费在线阅读，包含恋爱、校园、都市、甜宠、搞笑等全部分类，大结局章节在线畅读。"
    : currentCategory?.description || "";

  const changeSort = (key: SortKey) => {
    setSort(key);
  };

  const clearQuery = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("q");
    setSearchParams(next, { replace: true });
  };

  usePageMeta({
    title: pageTitle,
    description: pageDesc,
    keywords: `${isAll ? "全部漫画" : `${currentCategory?.name || ""}漫画`},免费阅读,在线看漫画,89890漫画网`,
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: pageTitle,
        description: pageDesc,
        url: `${SITE_BASE}/category/${slug}`,
        isPartOf: { "@id": `${SITE_BASE}/#website` },
        inLanguage: "zh-CN",
        mainEntity: {
          "@type": "ItemList",
          numberOfItems: sorted.length,
          itemListElement: sorted.slice(0, 12).map((comic, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: comic.title,
            url: `${SITE_BASE}/article/${comic.slug}`,
          })),
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_BASE}/` },
          { "@type": "ListItem", position: 2, name: "漫画分类", item: `${SITE_BASE}/category/all` },
          { "@type": "ListItem", position: 3, name: isAll ? "全部漫画" : `${currentCategory?.name || ""}漫画`, item: `${SITE_BASE}/category/${slug}` },
        ],
      },
    ],
  });

  return (
    <div className="pt-16">
      <Breadcrumb
        items={[
          { label: "漫画分类", to: "/category/all" },
          { label: isAll ? "全部漫画" : `${currentCategory?.name || ""}漫画` },
        ]}
      />

      <section className="container-site pb-14 md:pb-16">
        <div className="rounded-lg border border-background-200/70 bg-background-100/60 p-6 md:p-8">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            {isAll ? (query ? `搜索“${query}”` : "全部漫画") : `${currentCategory?.name || ""}漫画`}
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            {isAll
              ? query
                ? `找到 ${sorted.length} 部与“${query}”相关的作品，全部免费在线阅读。`
                : "汇聚全站漫画作品，恋爱、校园、都市、甜宠、搞笑一网打尽，完结作品大结局免费畅读。"
              : currentCategory?.description}
          </p>
          {query && (
            <button
              type="button"
              onClick={clearQuery}
              className="mt-3 inline-flex items-center gap-1 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700 hover:bg-primary-200 cursor-pointer"
            >
              <i className="ri-close-line" aria-hidden="true" />
              清除搜索：{query}
            </button>
          )}
        </div>

        <nav aria-label="漫画分类切换" className="mt-6 flex flex-wrap items-center gap-2">
          <Link
            to="/category/all"
            className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors cursor-pointer ${
              isAll ? "bg-primary-500 text-background-50" : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"
            }`}
          >
            全部
          </Link>
          {mangaCategories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors cursor-pointer ${
                slug === cat.slug ? "bg-primary-500 text-background-50" : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"
              }`}
            >
              {cat.name}
            </Link>
          ))}
        </nav>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-foreground-600 md:text-sm">
            共 <strong className="text-primary-600">{sorted.length}</strong> 部作品
          </p>
          <div className="flex items-center gap-1 rounded-full bg-secondary-100 p-1" role="tablist" aria-label="排序方式">
            {SORT_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                type="button"
                role="tab"
                aria-selected={sort === opt.key}
                onClick={() => changeSort(opt.key)}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-xs font-medium transition-colors cursor-pointer md:text-sm ${
                  sort === opt.key ? "bg-background-50 text-primary-600 shadow-soft" : "text-secondary-900 hover:text-primary-600"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {sorted.length > 0 ? (
          <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5 md:gap-5">
            {sorted.map((comic) => (
              <ComicCard key={comic.slug} comic={comic} />
            ))}
          </div>
        ) : (
          <div className="mt-10 flex flex-col items-center gap-3 rounded-lg border border-dashed border-background-300 bg-background-100/50 py-16 text-center">
            <i className="ri-file-search-line text-4xl text-foreground-400" aria-hidden="true" />
            <p className="text-sm text-foreground-600">没有找到相关漫画，换个关键词试试吧</p>
            <button type="button" onClick={clearQuery} className="btn-outline !py-2 text-sm">
              返回全部漫画
            </button>
          </div>
        )}
      </section>
    </div>
  );
}